import { useState, useEffect } from "react";

export default function SanFranciscoHeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* ─── Navbar ─── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm border-b border-gray-100" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-[70px]">
          <a href="https://fastpassportcenter.com" className="flex items-center">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Header.svg"
              alt="Fast Passport Center"
              className={`h-9 w-auto transition-all${scrolled ? "" : " brightness-0 invert"}`}
              style={scrolled ? undefined : { filter: "brightness(0) invert(1)" }}
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={`text-sm font-medium transition-colors whitespace-nowrap ${
                  scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
                }`}
              >
                {item.label}
              </a>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <a
              href="tel:8772530084"
              className={`hidden md:inline-flex items-center gap-1.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                scrolled ? "text-[#1A365D] hover:text-[#F6C344]" : "text-white/90 hover:text-[#F6C344]"
              }`}
            >
              <i className="ri-phone-line"></i>877-253-0084
            </a>
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] text-sm font-bold px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
            >
              Get Started
            </a>
            <button
              type="button"
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              className="md:hidden flex items-center justify-center w-9 h-9 cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              <i
                className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl ${
                  scrolled ? "text-[#1A365D]" : "text-white"
                }`}
              ></i>
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-3">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium text-[#1A365D] hover:text-[#F6C344] py-1"
              >
                {item.label}
              </a>
            ))}
            <a href="tel:8772530084" className="block text-sm font-semibold text-[#1A365D] py-1">
              <i className="ri-phone-line mr-1.5"></i>877-253-0084
            </a>
          </div>
        )}
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative overflow-hidden" style={{ minHeight: "700px" }}>
        {/* Background image */}
        <img
          src="https://readdy.ai/api/search-image?query=San%20Francisco%20skyline%20at%20golden%20hour%20with%20Golden%20Gate%20Bridge%20in%20the%20background%2C%20downtown%20skyscrapers%20and%20Transamerica%20Pyramid%20visible%2C%20warm%20sunset%20light%20reflecting%20off%20bay%20waters%2C%20Alcatraz%20Island%20in%20the%20distance%2C%20rolling%20fog%20over%20the%20Marin%20Headlands%2C%20warm%20amber%20and%20deep%20blue%20tones%2C%20professional%20architectural%20cityscape%20photography%2C%20panoramic%20aerial%20view%2C%20no%20visible%20text%20or%20people%2C%20iconic%20California%20coastal%20scene&width=1800&height=700&seq=sf-hero-001&orientation=landscape"
          alt="San Francisco skyline at golden hour with Golden Gate Bridge"
          className="absolute inset-0 w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/45"></div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pt-28 pb-16">
          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">

            {/* ── Left: copy ── */}
            <div className="flex-1 text-center lg:text-left">
              {/* Office badge */}
              <div className="inline-flex items-center gap-2 bg-[#F6C344]/15 border border-[#F6C344]/40 rounded-full px-4 py-1.5 mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] text-sm"></i>
                <span className="text-[#F6C344] text-xs font-semibold tracking-wide">
                  Physical Office — 447 Sutter St, Union Square, SF
                </span>
              </div>

              <h1 className="font-serif text-4xl md:text-5xl lg:text-[52px] font-bold text-white mb-5 leading-tight">
                Emergency &amp; Expedited Passport Services in San Francisco, CA
              </h1>

              <p className="text-white/80 text-base md:text-lg max-w-2xl lg:max-w-none mx-auto lg:mx-0 mb-8 leading-relaxed">
                SF founder heading to a Series A pitch in London in 4 days and your passport expired? UCSF researcher 
                deployed to a WHO field site in Geneva next week? SFO flight to Singapore on Friday and your passport 
                has less than 6 months of validity? We help San Francisco and Bay Area travelers get their expedited 
                passports as fast as 24 hours. As a registered U.S. Department of State courier &mdash; not a reseller 
                &mdash; we offer a best price guarantee and rates typically 30&ndash;100% lower than FedEx, Staples, 
                and other third-party passport courier resellers. A+ BBB rated.
              </p>

              <a
                href="https://fastpassportcenter.com/checkout/"
                className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap"
                style={{ transform: "translateZ(0)" }}
              >
                Start My Passport <i className="ri-arrow-right-line"></i>
              </a>

              {/* Trust badges */}
              <div className="mt-8">
                <div className="inline-flex flex-wrap items-center justify-center lg:justify-start gap-x-8 gap-y-3 bg-[#1A2B4A]/80 backdrop-blur-sm border border-white/10 rounded-lg px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[1,2,3,4,5].map((s) => (
                        <i key={s} className="ri-star-fill text-[#F6C344] text-base"></i>
                      ))}
                    </div>
                    <span className="text-white font-bold text-sm">5.0</span>
                    <span className="text-white/70 text-sm">Reviews</span>
                  </div>
                  <div className="hidden sm:block w-px h-5 bg-white/20"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-bold text-sm">BBB A+</span>
                    <span className="text-white/70 text-sm">Accredited</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Right: office photo card ── */}
            <div className="w-full lg:w-[320px] xl:w-[360px] flex-shrink-0">
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl overflow-hidden">
                <div className="relative" style={{ height: "240px" }}>
                  <img
                    src="https://storage.readdy-site.link/project_files/5b15eb5b-af79-4c03-9ad0-73c6095d69a7/7edcb049-084a-412e-9c66-9c91cfe5bf25_compressed_San-Francisco-fast-passport-center-office.webp"
                    alt="Fast Passport Center San Francisco office at Philippine Center, 447 Sutter St, Union Square"
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <span className="inline-flex items-center gap-1.5 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-2.5 py-1 rounded-full">
                      <i className="ri-map-pin-fill text-[10px]"></i>
                      Our San Francisco Office
                    </span>
                  </div>
                </div>
                <div className="px-4 py-4">
                  <p className="text-white font-semibold text-sm mb-1">Philippine Center, 447 Sutter St</p>
                  <p className="text-white/60 text-xs mb-3">Union Square &bull; San Francisco, CA 94108</p>
                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center gap-2 text-white/70 text-xs">
                      <i className="ri-time-line text-[#F6C344] text-sm flex-shrink-0"></i>
                      <span>Mon&ndash;Fri: 9 AM &ndash; 6 PM</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70 text-xs">
                      <i className="ri-calendar-line text-[#F6C344] text-sm flex-shrink-0"></i>
                      <span>By appointment only &mdash; no walk-ins</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/70 text-xs">
                      <i className="ri-train-line text-[#F6C344] text-sm flex-shrink-0"></i>
                      <span>0.2 mi to Powell St BART Station</span>
                    </div>
                  </div>
                  <a
                    href="https://maps.google.com/maps?daddr=447+Sutter+St+San+Francisco+CA+94108"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold py-2 rounded-md transition-all whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i> Get Directions
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
}